import { computed, nextTick, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { omit } from 'lodash-es'
import { useLocalStorage } from '@vueuse/core'
import { useApi } from 'soykajs/composables/api'
import { useCookies } from '@vueuse/integrations/useCookies'
import { jwtDecode } from 'jwt-decode'
import config from '@/config'
import { i18n } from '@/i18n'
import { resolveLocaleForUser, resetLocaleToDefaultForUser } from '@/i18n/localeStorage.js'

export const useUserStore = defineStore('user', () => {
  const user = useLocalStorage('user', {})
  const lastActivity = useLocalStorage('lastActivity', Date.now())
  const isAuthorized = computed(() => user.value?.access_token?.length > 0)
  const cookies = useCookies(['AuthorizationToken'])
  const isValid = computed(() => user.value?.fio && user.value?.email_notifications)
  const isLoggedIn = ref(false)

  let logoutInterval = null
  const TIMEOUT = Number(config.VITE_LOGOUT_TIMEOUT ?? 900000)
  const maxAge = TIMEOUT == -1 ? 34560000 : TIMEOUT / 1000
  let currentCallbackPromise = null

  // initial value may be set by SSO redirect url
  const atFromCookie = cookies.get('AuthorizationToken')
  if (atFromCookie) {
    user.value = { ...user.value, access_token: atFromCookie }
  }

  if (user.value?.id) {
    i18n.global.locale.value = resolveLocaleForUser(user.value)
  }

  watch(
    isAuthorized,
    async (curr, prev) => {
      // login
      if (curr && !prev) {
        const jwt = user.value.access_token
        const jwtd = jwtDecode(jwt)

        user.value = { ...user.value, sso: jwtd.sso }
        cookies.set('AuthorizationToken', jwt, { path: '/', maxAge })
        await nextTick()
        await fetchCurrent(prev === false)
        isLoggedIn.value = true
      }
      // lougout
      if (!curr && prev) {
        cookies.remove('AuthorizationToken', { path: '/' })
        isLoggedIn.value = false
      }
    },
    { immediate: true }
  )

  async function fetchLtmsPreferences() {
    const { refresh, model } = useApi({
      url: 'profile/preferences',
      data: { prefer_english: true },
    })
    try {
      await refresh()
      return model.value?.prefer_english !== false
    } catch {
      return user.value?.prefer_english !== false
    }
  }

  /**
   * @param {boolean} [resetLocale] true после новой авторизации — сбросить язык к дефолту.
   */
  async function fetchCurrent(resetLocale = false) {
    const { refresh: _fetchCurrentUser, model: _currentUser } = useApi({
      url: 'current_user',
      query: {'service': 'ltms'},
      pick(d) {
        return omit(d, ['deleted', 'ms_entra'])
      },
    })
    await _fetchCurrentUser()
    const preferEnglish = await fetchLtmsPreferences()
    isLoggedIn.value = true
    user.value = { ...user.value, ..._currentUser.value, prefer_english: preferEnglish }
    i18n.global.locale.value = resetLocale
      ? resetLocaleToDefaultForUser(user.value)
      : resolveLocaleForUser(user.value)
  }

  // with children
  function can(roleName) {
    return hasRole(false, user.value?.roles, roleName)
  }

  // without children
  function is(roleName) {
    return hasRole(true, user.value?.roles, roleName)
  }

  function updateLastActivity() {
    lastActivity.value = Date.now()
  }

  function startLogoutChecker(callback) {
    stopLogoutChecker()
    logoutInterval = setInterval(async () => {
      if (TIMEOUT == -1) {
        return
      }
      if (currentCallbackPromise) {
        return
      }
      if (Date.now() - lastActivity.value > TIMEOUT) {
        stopLogoutChecker()
        currentCallbackPromise = callback().finally(() => {
          currentCallbackPromise = null
        })
      }
    }, 10000)
  }

  function stopLogoutChecker() {
    if (logoutInterval) {
      clearInterval(logoutInterval)
      logoutInterval = null
    }
  }

  function resetTimer(callback) {
    updateLastActivity()
    startLogoutChecker(callback)
    const jwt = user.value?.access_token
    if (jwt) {
      cookies.set('AuthorizationToken', jwt, {
        path: '/',
        maxAge,
      })
    }
  }

  return {
    user,
    isAuthorized,
    isValid,
    lastActivity,
    isLoggedIn,
    fetchCurrent,
    can,
    is,
    resetTimer,
    updateLastActivity,
    startLogoutChecker,
    stopLogoutChecker,
  }
})

export function hasRole(strict = false, roles = [], rname) {
  if (!Array.isArray(roles)) {
    return false
  }
  const _rname = Array.isArray(rname) ? rname : [rname]
  for (let role of roles) {
    if (role?.service !== 'ltms') continue
    const _hasrole = _rname.includes(role.name)
    if (strict === true) {
      return _hasrole
    }
    if (_hasrole || hasRole(false, role.children, _rname) === true) {
      return true
    }
  }
  return false
}
